import { easeCubicInOut, select } from "d3";
import type { Selection } from "d3";

import {
  AppletElement,
  EdgeEndpoints,
  GROWTH_BACKGROUND_EDGE_STROKE,
  GROWTH_BACKGROUND_NODE_FILL,
  GROWTH_BACKGROUND_NODE_STROKE,
  GroupSelection,
  NODE_STROKE,
  Point,
  appendStepControls,
  renderApplet,
  trimmedEdgeEndpoints,
} from "./common";

type DoubleStarProofNodeKind = "center" | "leaf";

type DoubleStarProofNode = Readonly<{
  id: string;
  label: number;
  startX: number;
  startY: number;
  finalX: number;
  finalY: number;
  finalKind: DoubleStarProofNodeKind;
}>;

type DoubleStarProofEdge = Readonly<{
  source: string;
  target: string;
}>;

type DoubleStarProofTextPart = Readonly<{
  text: string;
  italic?: boolean;
  subscript?: boolean;
}>;

type DoubleStarProofFrame = Readonly<{
  step: number;
  title: string;
  titleParts?: readonly DoubleStarProofTextPart[];
  detail: string;
  detailParts?: readonly DoubleStarProofTextPart[];
  activeEdges: readonly DoubleStarProofEdge[];
  activeFinalNodeIds: readonly string[];
  highlightedNodeIds: readonly string[];
  settledLayout?: boolean;
  yIsCenter: boolean;
}>;

const DOUBLE_STAR_PROOF_WIDTH = 620;
const DOUBLE_STAR_PROOF_HEIGHT = 270;
const MOVE_TRANSITION_MS = 1000;
const CENTER_RADIUS = 18;
const LEAF_RADIUS = 15;
const CENTER_FILL = "#8fbc8f";
const LEAF_FILL = "#c9ddc5";
const HIGHLIGHT_FILL = "#f1dda3";
const HIGHLIGHT_STROKE = "#9b6b16";
const ACTIVE_EDGE_STROKE = "#444";
const ACTIVE_LABEL_FILL = "#263726";
const EDGE_LABEL_FILL = "#5d5d5d";
const EDGE_LABEL_BACKGROUND_FILL = "#f7f8f4";
const ANNOTATION_FILL = "#445044";

const DOUBLE_STAR_PROOF_NODES: readonly DoubleStarProofNode[] = [
  { id: "x", label: 0, startX: 214, startY: 112, finalX: 214, finalY: 112, finalKind: "center" },
  { id: "n1", label: 1, startX: 214, startY: 28, finalX: 528, finalY: 70, finalKind: "leaf" },
  { id: "n2", label: 2, startX: 273.397, startY: 52.603, finalX: 542, finalY: 112, finalKind: "leaf" },
  { id: "n3", label: 3, startX: 273.397, startY: 171.397, finalX: 528, finalY: 154, finalKind: "leaf" },
  { id: "y", label: 4, startX: 298, startY: 112, finalX: 374, finalY: 112, finalKind: "center" },
  { id: "n5", label: 5, startX: 214, startY: 196, finalX: 214, finalY: 196, finalKind: "leaf" },
  { id: "n6", label: 6, startX: 154.603, startY: 171.397, finalX: 154.603, finalY: 171.397, finalKind: "leaf" },
  { id: "n7", label: 7, startX: 130, startY: 112, finalX: 130, finalY: 112, finalKind: "leaf" },
  { id: "n8", label: 8, startX: 154.603, startY: 52.603, finalX: 154.603, finalY: 52.603, finalKind: "leaf" },
];

const DOPPELGANGER_NODE_IDS: readonly string[] = ["y", "n1", "n2", "n3"];

const FINAL_DOUBLE_STAR_EDGES: readonly DoubleStarProofEdge[] = [
  { source: "x", target: "y" },
  { source: "x", target: "n5" },
  { source: "x", target: "n6" },
  { source: "x", target: "n7" },
  { source: "x", target: "n8" },
  { source: "y", target: "n1" },
  { source: "y", target: "n2" },
  { source: "y", target: "n3" },
];

const INITIAL_STAR_EDGES: readonly DoubleStarProofEdge[] = [
  { source: "x", target: "y" },
  { source: "x", target: "n1" },
  { source: "x", target: "n2" },
  { source: "x", target: "n3" },
  { source: "x", target: "n5" },
  { source: "x", target: "n6" },
  { source: "x", target: "n7" },
  { source: "x", target: "n8" },
];

const AFTER_PAIR_TRANSFER_EDGES: readonly DoubleStarProofEdge[] = [
  { source: "x", target: "y" },
  { source: "x", target: "n2" },
  { source: "x", target: "n5" },
  { source: "x", target: "n6" },
  { source: "x", target: "n7" },
  { source: "x", target: "n8" },
  { source: "y", target: "n1" },
  { source: "y", target: "n3" },
];

const DOUBLE_STAR_PROOF_FRAMES: readonly DoubleStarProofFrame[] = [
  {
    step: 0,
    title: "Start with K1,8",
    titleParts: [{ text: "Start with K" }, { text: "1,8", subscript: true }],
    detail: "",
    activeEdges: INITIAL_STAR_EDGES,
    activeFinalNodeIds: [],
    highlightedNodeIds: [],
    yIsCenter: false,
  },
  {
    step: 1,
    title: "Choose label 4",
    detail: "Since b is odd, choose the leaf labeled 4 to become the second center.",
    detailParts: [
      { text: "Since " },
      { text: "b", italic: true },
      { text: " is odd, choose the leaf labeled 4 to become the second center." },
    ],
    activeEdges: INITIAL_STAR_EDGES,
    activeFinalNodeIds: ["y"],
    highlightedNodeIds: ["y"],
    yIsCenter: true,
  },
  {
    step: 2,
    title: "Leaf pair transfer",
    detail: "Move leaves 1 and 3 to the new center because 1 + 3 = 4.",
    activeEdges: AFTER_PAIR_TRANSFER_EDGES,
    activeFinalNodeIds: ["y", "n1", "n3"],
    highlightedNodeIds: ["n1", "n3", "y"],
    yIsCenter: true,
  },
  {
    step: 3,
    title: "Leaf midpoint transfer",
    detail: "Move leaf 2 to the new center because 2 + 2 = 4, so its edge label stays 2.",
    activeEdges: FINAL_DOUBLE_STAR_EDGES,
    activeFinalNodeIds: ["y", "n1", "n2", "n3"],
    highlightedNodeIds: ["n2", "y"],
    yIsCenter: true,
  },
  {
    step: 4,
    title: "Result: D4,3",
    titleParts: [{ text: "Result: D" }, { text: "4,3", subscript: true }],
    detail: "The new center has 3 leaves, the original center has 4, and edge labels are still 1 through 8.",
    activeEdges: FINAL_DOUBLE_STAR_EDGES,
    activeFinalNodeIds: ["y", "n1", "n2", "n3"],
    highlightedNodeIds: [],
    settledLayout: true,
    yIsCenter: true,
  },
];

function nodeLookup(): ReadonlyMap<string, DoubleStarProofNode> {
  return new Map(
    DOUBLE_STAR_PROOF_NODES.map((node: DoubleStarProofNode): [string, DoubleStarProofNode] => [node.id, node]),
  );
}

function edgeKey(edge: DoubleStarProofEdge): string {
  return `${edge.source}-${edge.target}`;
}

function edgeKeys(edges: readonly DoubleStarProofEdge[]): ReadonlySet<string> {
  return new Set(edges.map((edge: DoubleStarProofEdge): string => edgeKey(edge)));
}

function transitionEdges(
  frame: DoubleStarProofFrame,
  previousFrame?: DoubleStarProofFrame,
): readonly DoubleStarProofEdge[] {
  if (previousFrame === undefined) {
    return frame.activeEdges;
  }

  const edges = new Map<string, DoubleStarProofEdge>();
  previousFrame.activeEdges.forEach((edge: DoubleStarProofEdge): void => {
    edges.set(edgeKey(edge), edge);
  });
  frame.activeEdges.forEach((edge: DoubleStarProofEdge): void => {
    edges.set(edgeKey(edge), edge);
  });
  return Array.from(edges.values());
}

function nodeKind(frame: DoubleStarProofFrame, node: DoubleStarProofNode): DoubleStarProofNodeKind {
  if (node.id === "y") {
    return frame.yIsCenter ? "center" : "leaf";
  }

  return node.finalKind;
}

function nodeRadius(frame: DoubleStarProofFrame, node: DoubleStarProofNode): number {
  return nodeKind(frame, node) === "center" ? CENTER_RADIUS : LEAF_RADIUS;
}

function settledNodePoint(node: DoubleStarProofNode): Point {
  switch (node.id) {
    case "x":
      return { x: 184, y: 112 };
    case "n5":
      return { x: 214, y: 203 };
    case "n6":
      return { x: 106, y: 168 };
    case "n7":
      return { x: 106, y: 56 };
    case "n8":
      return { x: 214, y: 21 };
    default:
      return { x: node.finalX, y: node.finalY };
  }
}

function nodePoint(frame: DoubleStarProofFrame, node: DoubleStarProofNode): Point {
  if (frame.settledLayout === true) {
    return settledNodePoint(node);
  }

  if (frame.activeFinalNodeIds.includes(node.id)) {
    return { x: node.finalX, y: node.finalY };
  }

  return { x: node.startX, y: node.startY };
}

function finalNodeRadius(node: DoubleStarProofNode): number {
  return node.finalKind === "center" ? CENTER_RADIUS : LEAF_RADIUS;
}

function edgeEndpoints(
  edge: DoubleStarProofEdge,
  nodes: ReadonlyMap<string, DoubleStarProofNode>,
  frame: DoubleStarProofFrame,
): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(
    nodePoint(frame, source),
    nodePoint(frame, target),
    nodeRadius(frame, source),
    nodeRadius(frame, target),
  );
}

function finalEdgeEndpoints(
  edge: DoubleStarProofEdge,
  nodes: ReadonlyMap<string, DoubleStarProofNode>,
): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(
    { x: source.finalX, y: source.finalY },
    { x: target.finalX, y: target.finalY },
    finalNodeRadius(source),
    finalNodeRadius(target),
  );
}

function edgeLabel(edge: DoubleStarProofEdge, nodes: ReadonlyMap<string, DoubleStarProofNode>): string {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return "";
  }

  return Math.abs(source.label - target.label).toString();
}

function edgeLabelPosition(
  edge: DoubleStarProofEdge,
  nodes: ReadonlyMap<string, DoubleStarProofNode>,
  frame: DoubleStarProofFrame,
): Point {
  const endpoints = edgeEndpoints(edge, nodes, frame);
  return {
    x: (endpoints.x1 + endpoints.x2) * 0.5,
    y: (endpoints.y1 + endpoints.y2) * 0.5,
  };
}

function activeEdgePath(
  edge: DoubleStarProofEdge,
  nodes: ReadonlyMap<string, DoubleStarProofNode>,
  frame: DoubleStarProofFrame,
): string {
  const endpoints = edgeEndpoints(edge, nodes, frame);
  return `M ${endpoints.x1} ${endpoints.y1} L ${endpoints.x2} ${endpoints.y2}`;
}

function nodeFill(frame: DoubleStarProofFrame, node: DoubleStarProofNode): string {
  if (frame.highlightedNodeIds.includes(node.id)) {
    return HIGHLIGHT_FILL;
  }

  return nodeKind(frame, node) === "center" ? CENTER_FILL : LEAF_FILL;
}

function doppelgangerNodes(
  frame: DoubleStarProofFrame,
  nodes: ReadonlyMap<string, DoubleStarProofNode>,
): readonly DoubleStarProofNode[] {
  return DOPPELGANGER_NODE_IDS.reduce<DoubleStarProofNode[]>((doppelgangers, nodeId: string): DoubleStarProofNode[] => {
    const node = nodes.get(nodeId);
    if (node !== undefined && !frame.activeFinalNodeIds.includes(nodeId)) {
      doppelgangers.push(node);
    }

    return doppelgangers;
  }, []);
}

function doppelgangerEdges(frame: DoubleStarProofFrame): readonly DoubleStarProofEdge[] {
  const inactiveNodeIds = new Set(
    DOPPELGANGER_NODE_IDS.filter((nodeId: string): boolean => !frame.activeFinalNodeIds.includes(nodeId)),
  );
  const edges: DoubleStarProofEdge[] = [];

  if (inactiveNodeIds.has("y")) {
    edges.push({ source: "x", target: "y" });
  }

  ["n1", "n2", "n3"].forEach((nodeId: string): void => {
    if (inactiveNodeIds.has(nodeId)) {
      edges.push({ source: "y", target: nodeId });
    }
  });

  return edges;
}

function renderDoppelgangers(graph: GroupSelection, frame: DoubleStarProofFrame): void {
  const nodes = nodeLookup();
  const doppelgangers = graph.append<SVGGElement>("g")
    .attr("class", "double-star-proof-doppelgangers")
    .attr("opacity", 0.58)
    .attr("pointer-events", "none");

  doppelgangers.append<SVGGElement>("g")
    .attr("class", "double-star-proof-doppelganger-edges")
    .selectAll<SVGLineElement, DoubleStarProofEdge>("line")
    .data(doppelgangerEdges(frame))
    .join("line")
    .attr("x1", (edge: DoubleStarProofEdge): number => finalEdgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: DoubleStarProofEdge): number => finalEdgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: DoubleStarProofEdge): number => finalEdgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: DoubleStarProofEdge): number => finalEdgeEndpoints(edge, nodes).y2)
    .attr("stroke", GROWTH_BACKGROUND_EDGE_STROKE)
    .attr("stroke-width", 1.15)
    .attr("stroke-dasharray", "4 4")
    .attr("stroke-linecap", "round");

  const nodeGroups = doppelgangers.append<SVGGElement>("g")
    .attr("class", "double-star-proof-doppelganger-nodes")
    .selectAll<SVGGElement, DoubleStarProofNode>("g")
    .data(doppelgangerNodes(frame, nodes))
    .join("g")
    .attr("transform", (node: DoubleStarProofNode): string => `translate(${node.finalX},${node.finalY})`);

  nodeGroups.append<SVGCircleElement>("circle")
    .attr("r", (node: DoubleStarProofNode): number => finalNodeRadius(node))
    .attr("fill", GROWTH_BACKGROUND_NODE_FILL)
    .attr("stroke", GROWTH_BACKGROUND_NODE_STROKE)
    .attr("stroke-width", 1.1);
}

function renderActiveEdges(
  graph: GroupSelection,
  frame: DoubleStarProofFrame,
  previousFrame?: DoubleStarProofFrame,
): void {
  const nodes = nodeLookup();
  const previousActiveEdgeKeys = edgeKeys(previousFrame?.activeEdges ?? []);
  const currentActiveEdgeKeys = edgeKeys(frame.activeEdges);
  const paths = graph.append<SVGGElement>("g")
    .attr("class", "double-star-proof-active-edges")
    .selectAll<SVGPathElement, DoubleStarProofEdge>("path")
    .data(transitionEdges(frame, previousFrame))
    .join("path")
    .attr("data-edge-key", (edge: DoubleStarProofEdge): string => edgeKey(edge))
    .attr("d", (edge: DoubleStarProofEdge): string => activeEdgePath(edge, nodes, previousFrame ?? frame))
    .attr("fill", "none")
    .attr("stroke", ACTIVE_EDGE_STROKE)
    .attr("stroke-width", 1.45)
    .attr("opacity", (edge: DoubleStarProofEdge): number => {
      if (previousFrame === undefined || previousActiveEdgeKeys.has(edgeKey(edge))) {
        return 1;
      }

      return 0;
    })
    .attr("stroke-linecap", "round");

  if (previousFrame === undefined) {
    return;
  }

  paths.transition()
    .duration(MOVE_TRANSITION_MS)
    .ease(easeCubicInOut)
    .attr("d", (edge: DoubleStarProofEdge): string => activeEdgePath(edge, nodes, frame))
    .attr("opacity", (edge: DoubleStarProofEdge): number => currentActiveEdgeKeys.has(edgeKey(edge)) ? 1 : 0)
    .on("end", function removeInactiveEdge(edge: DoubleStarProofEdge): void {
      if (!currentActiveEdgeKeys.has(edgeKey(edge))) {
        select(this).remove();
      }
    });
}

function renderEdgeLabels(
  graph: GroupSelection,
  frame: DoubleStarProofFrame,
  previousFrame?: DoubleStarProofFrame,
): void {
  const nodes = nodeLookup();
  const previousActiveEdgeKeys = edgeKeys(previousFrame?.activeEdges ?? []);
  const currentActiveEdgeKeys = edgeKeys(frame.activeEdges);
  const edgeLabels = graph.append<SVGGElement>("g")
    .attr("class", "double-star-proof-edge-labels")
    .selectAll<SVGGElement, DoubleStarProofEdge>("g")
    .data(transitionEdges(frame, previousFrame))
    .join("g")
    .attr("data-edge-key", (edge: DoubleStarProofEdge): string => edgeKey(edge))
    .attr("opacity", (edge: DoubleStarProofEdge): number => {
      if (previousFrame === undefined || previousActiveEdgeKeys.has(edgeKey(edge))) {
        return 1;
      }

      return 0;
    })
    .attr("transform", (edge: DoubleStarProofEdge): string => {
      const point = edgeLabelPosition(edge, nodes, previousFrame ?? frame);
      return `translate(${point.x},${point.y})`;
    });

  edgeLabels.append<SVGCircleElement>("circle")
    .attr("r", 10.5)
    .attr("fill", EDGE_LABEL_BACKGROUND_FILL)
    .attr("stroke", "none");

  edgeLabels.append<SVGTextElement>("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("font-family", "Avenir Next, Helvetica, Arial, sans-serif")
    .attr("font-size", 12)
    .attr("fill", EDGE_LABEL_FILL)
    .text((edge: DoubleStarProofEdge): string => edgeLabel(edge, nodes));

  if (previousFrame === undefined) {
    return;
  }

  edgeLabels.transition()
    .duration(MOVE_TRANSITION_MS)
    .ease(easeCubicInOut)
    .attr("opacity", (edge: DoubleStarProofEdge): number => currentActiveEdgeKeys.has(edgeKey(edge)) ? 1 : 0)
    .attr("transform", (edge: DoubleStarProofEdge): string => {
      const point = edgeLabelPosition(edge, nodes, frame);
      return `translate(${point.x},${point.y})`;
    })
    .on("end", function removeInactiveEdgeLabel(edge: DoubleStarProofEdge): void {
      if (!currentActiveEdgeKeys.has(edgeKey(edge))) {
        select(this).remove();
      }
    });
}

function nodeStrokeWidth(frame: DoubleStarProofFrame, node: DoubleStarProofNode): number {
  if (frame.highlightedNodeIds.includes(node.id)) {
    return 2.3;
  }

  return nodeKind(frame, node) === "center" ? 1.8 : 1.3;
}

function nodeStroke(frame: DoubleStarProofFrame, node: DoubleStarProofNode): string {
  return frame.highlightedNodeIds.includes(node.id) ? HIGHLIGHT_STROKE : NODE_STROKE;
}

function nodeLabelFontSize(frame: DoubleStarProofFrame, node: DoubleStarProofNode): number {
  return nodeKind(frame, node) === "center" ? 17 : 14;
}

function renderNodes(
  graph: GroupSelection,
  frame: DoubleStarProofFrame,
  previousFrame?: DoubleStarProofFrame,
): void {
  const initialFrame = previousFrame ?? frame;
  const nodeGroups = graph.append<SVGGElement>("g")
    .attr("class", "double-star-proof-nodes")
    .selectAll<SVGGElement, DoubleStarProofNode>("g")
    .data(DOUBLE_STAR_PROOF_NODES)
    .join("g")
    .attr("transform", (node: DoubleStarProofNode): string => {
      const point = nodePoint(initialFrame, node);
      return `translate(${point.x},${point.y})`;
    });

  nodeGroups.append<SVGCircleElement>("circle")
    .attr("r", (node: DoubleStarProofNode): number => nodeRadius(initialFrame, node))
    .attr("fill", (node: DoubleStarProofNode): string => nodeFill(initialFrame, node))
    .attr("stroke", (node: DoubleStarProofNode): string => nodeStroke(initialFrame, node))
    .attr("stroke-width", (node: DoubleStarProofNode): number => nodeStrokeWidth(initialFrame, node));

  nodeGroups.append<SVGTextElement>("text")
    .attr("text-anchor", "middle")
    .attr("dy", "0.34em")
    .attr("font-family", "Times, serif")
    .attr("font-size", (node: DoubleStarProofNode): number => nodeLabelFontSize(initialFrame, node))
    .attr("fill", ACTIVE_LABEL_FILL)
    .text((node: DoubleStarProofNode): string => node.label.toString());

  if (previousFrame === undefined) {
    return;
  }

  nodeGroups.transition()
    .duration(MOVE_TRANSITION_MS)
    .ease(easeCubicInOut)
    .attr("transform", (node: DoubleStarProofNode): string => {
      const point = nodePoint(frame, node);
      return `translate(${point.x},${point.y})`;
    });

  nodeGroups.select<SVGCircleElement>("circle")
    .transition()
    .duration(MOVE_TRANSITION_MS)
    .ease(easeCubicInOut)
    .attr("r", (node: DoubleStarProofNode): number => nodeRadius(frame, node))
    .attr("fill", (node: DoubleStarProofNode): string => nodeFill(frame, node))
    .attr("stroke", (node: DoubleStarProofNode): string => nodeStroke(frame, node))
    .attr("stroke-width", (node: DoubleStarProofNode): number => nodeStrokeWidth(frame, node));

  nodeGroups.select<SVGTextElement>("text")
    .transition()
    .duration(MOVE_TRANSITION_MS)
    .ease(easeCubicInOut)
    .attr("font-size", (node: DoubleStarProofNode): number => nodeLabelFontSize(frame, node));
}

function renderTextParts(
  text: Selection<SVGTextElement, unknown, null, undefined>,
  parts: readonly DoubleStarProofTextPart[],
): void {
  parts.forEach((part: DoubleStarProofTextPart): void => {
    const tspan = text.append<SVGTSpanElement>("tspan")
      .text(part.text);

    if (part.italic === true) {
      tspan.attr("font-style", "italic");
    }

    if (part.subscript === true) {
      tspan
        .attr("font-size", 10)
        .attr("dy", "0.35em");
    }
  });
}

function renderFrameText(graph: GroupSelection, frame: DoubleStarProofFrame): void {
  const textLayer = graph.append<SVGGElement>("g")
    .attr("class", "double-star-proof-annotations");
  const titleY = frame.detail.length === 0 ? 230 : 218;

  const title = textLayer.append<SVGTextElement>("text")
    .attr("x", DOUBLE_STAR_PROOF_WIDTH * 0.5)
    .attr("y", titleY)
    .attr("text-anchor", "middle")
    .attr("font-family", "Avenir Next, Helvetica, Arial, sans-serif")
    .attr("font-size", 14)
    .attr("font-weight", 600)
    .attr("fill", ANNOTATION_FILL);

  if (frame.titleParts === undefined) {
    title.text(frame.title);
  } else {
    renderTextParts(title, frame.titleParts);
  }

  if (frame.detail.length === 0) {
    return;
  }

  const detail = textLayer.append<SVGTextElement>("text")
    .attr("x", DOUBLE_STAR_PROOF_WIDTH * 0.5)
    .attr("y", 240)
    .attr("text-anchor", "middle")
    .attr("font-family", "Avenir Next, Helvetica, Arial, sans-serif")
    .attr("font-size", 13)
    .attr("fill", ANNOTATION_FILL);

  if (frame.detailParts === undefined) {
    detail.text(frame.detail);
  } else {
    renderTextParts(detail, frame.detailParts);
  }
}

function renderDoubleStarProofFrame(
  graph: GroupSelection,
  frame: DoubleStarProofFrame,
  element: AppletElement,
  previousFrame?: DoubleStarProofFrame,
): void {
  element.dataset.doubleStarStep = frame.step.toString();
  element.dataset.doubleStarMax = (DOUBLE_STAR_PROOF_FRAMES.length - 1).toString();
  const transitionFrame = previousFrame?.step === frame.step ? undefined : previousFrame;
  graph.selectAll("*").interrupt().remove();

  renderDoppelgangers(graph, frame);
  renderActiveEdges(graph, frame, transitionFrame);
  renderEdgeLabels(graph, frame, transitionFrame);
  renderNodes(graph, frame, transitionFrame);
  renderFrameText(graph, frame);
}

function renderDoubleStarProof(element: AppletElement): void {
  element.dataset.rendered = "true";
  element.replaceChildren();

  const svg = select<AppletElement, unknown>(element)
    .append<SVGSVGElement>("svg")
    .attr("viewBox", `0 0 ${DOUBLE_STAR_PROOF_WIDTH} ${DOUBLE_STAR_PROOF_HEIGHT}`)
    .attr("width", DOUBLE_STAR_PROOF_WIDTH)
    .attr("height", DOUBLE_STAR_PROOF_HEIGHT)
    .attr("role", "img")
    .attr("aria-label", "Step-by-step proof that the double star D four comma three is graceful.");

  const graph = svg.append<SVGGElement>("g")
    .attr("class", "double-star-proof-graph");
  let previousFrame: DoubleStarProofFrame | undefined;

  appendStepControls({
    element,
    frameCount: DOUBLE_STAR_PROOF_FRAMES.length,
    onFrameChange: (frameIndex: number): void => {
      const frame = DOUBLE_STAR_PROOF_FRAMES[frameIndex];
      renderDoubleStarProofFrame(graph, frame, element, previousFrame);
      previousFrame = frame;
    },
  });
}

renderApplet(".double-star-proof-applet", renderDoubleStarProof);
